import { cn } from '@/lib/utils';
import type { CMSColumn } from '../schemas';
import { match } from 'ts-pattern';
import { CMSCard } from '../cms-card';
import { CMSColumns } from '../cms-columns';
import { CMSTextAdvanced } from '../cms-text-advanced';
import { useResponsive } from '@/hooks/use-responsive';
import { CMSImage } from '../cms-image';
import { generateGridOrder } from '@/utils/cms';
import React, { Suspense } from 'react';

const LazyCMSVideo = React.lazy(() => import('../cms-video'));

interface CMSColumnsStandardProps {
  data: CMSColumn;
  className?: string;
}
const CMSColumnsStandard = ({ data, className }: CMSColumnsStandardProps) => {
  const { isDesktop, isMobile } = useResponsive();
  const itemsCount = data.items.length;

  return (
    <div
      data-component="CMSColumnsStandard"
      className={cn(
        'grid gap-6 md:gap-4 lg:gap-8 justify-items-center',
        'grid-cols-1', // 1 column for mobile always
        // Medium screens: max 2 columns
        {
          'md:grid-cols-1': itemsCount === 1,
          'md:grid-cols-2': itemsCount >= 2,
        },
        // Large screens: respect both max_per_row and itemsCount
        {
          'lg:grid-cols-1': data.max_per_row === 1 || itemsCount === 1,
          'lg:grid-cols-2':
            (data.max_per_row >= 2 && itemsCount === 2) ||
            (data.max_per_row === 2 && itemsCount > 2),
          'lg:grid-cols-3': data.max_per_row >= 3 && itemsCount >= 3,
          'lg:grid-cols-4': data.max_per_row === 4 && itemsCount >= 4,
        },

        // Change it to justify item start if it has children component "TrustPilotWidget"
        '[&:has([data-component="TrustPilotWidget"])]:justify-items-start',

        // Add container if it has children component "TrustPilotWidget"

        //Add margin top for CMSColumnsStandard
        `my-6 md:my-8 lg:my-16`,

        //Add padding top if it has children component "TrustPilotWidget"
        `[&:has([data-component="TrustPilotWidget"])]:mt-8 
         md:[&:has([data-component="TrustPilotWidget"])]:mt-12 
         lg:[&:has([data-component="TrustPilotWidget"])]:mt-20`,

        // Add margin top to h1 tag itself if it is not first child inside CMSColumnsStandard
        `[&_[data-component="CMSText"]_h1:not(:first-child)]:mt-8
         md:[&_[data-component="CMSText"]_h1:not(:first-child)]:mt-12 
         lg:[&_[data-component="CMSText"]_h1:not(:first-child)]:mt-20`,

        // Add margin top to h2 tag itself if it is not first child inside CMSColumnsStandard
        `[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-8 
         md:[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-8 
         lg:[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-8`,

        // Add margin top to h3 tag itself if it is not first child inside CMSColumnsStandard
        `[&_[data-component="CMSText"]_h3:not(:first-child)]:mt-6 
          md:[&_[data-component="CMSText"]_h3:not(:first-child)]:mt-8`,

        // Align text to middle if CMSTextAdvanced has a previous or next sibling CMSImage
        '[&_[data-component="CMSTextAdvanced"]:has(+[data-component="CMSImage"])]:flex [&_[data-component="CMSTextAdvanced"]:has(+[data-component="CMSImage"])]:flex-col [&_[data-component="CMSTextAdvanced"]:has(+[data-component="CMSImage"])]:justify-center',
        '[&_[data-component="CMSImage"]+[data-component="CMSTextAdvanced"]]:flex [&_[data-component="CMSImage"]+[data-component="CMSTextAdvanced"]]:flex-col [&_[data-component="CMSImage"]+[data-component="CMSTextAdvanced"]]:justify-center',

        // Set height min content for CMSCard if it has sibling of CMSTextAdvanced,
        'md:[&_[data-component="CMSCard"]:has(+[data-component="CMSTextAdvanced"])]:h-min md:[&_[data-component="CMSTextAdvanced"]+[data-component="CMSCard"]]:h-min',

        className
      )}
    >
      {data.items.map((item) =>
        match(item)
          .with({ type: 'card' }, (item) => {
            const style = generateGridOrder({
              itemOrder: item.item_order,
              isEnabled: isMobile,
            });
            return (
              <CMSCard
                style={style}
                // Add text-2xl for h3(eg. Manage your service on the app, Refer a friend to get a discount)  and margin top for p tag in large breakpoint
                // lg:[&_[data-component="CMSText"]_h3]:text-2xl doesn't work for some reason
                className={
                  isDesktop
                    ? '[&_[data-component="CMSText"]_h3]:!text-2xl [&_[data-component="CMSText"]_p]:!mt-4'
                    : ''
                }
                key={item.id}
                cardData={item}
              />
            );
          })
          .with({ type: 'columns' }, (item) => (
            <CMSColumns key={item.id} columnsData={item} />
          ))
          .with({ type: 'text_advanced' }, (textAdvanced) => {
            const style = generateGridOrder({
              itemOrder: textAdvanced.item_order,
              isEnabled: isMobile,
            });

            return (
              <CMSTextAdvanced
                style={style}
                // Add justify self end if CMSTextAdvanced has Trustpilot widget
                className="md:[&:has(.TrustPilotWidget)]:justify-self-end"
                key={textAdvanced.id}
                textAdvancedData={textAdvanced}
              />
            );
          })
          .with({ type: 'image' } as const, (data) => {
            const style = generateGridOrder({
              itemOrder: data.item_order,
              isEnabled: isMobile,
            });

            return <CMSImage style={style} key={item.id} imageData={data} />;
          })
          .with({ type: 'video' } as const, (data) => {
            const style = generateGridOrder({
              itemOrder: data.item_order,
              isEnabled: isMobile,
            });
            return (
              <Suspense fallback={null} key={item.id}>
                <LazyCMSVideo style={style} data={data} />
              </Suspense>
            );
          })
          .otherwise(() => null)
      )}
    </div>
  );
};
export { CMSColumnsStandard };
